import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  setCurrentSurvey,
  setCurrentSurveyGroup,
  setError,
} from "@/features/auth/interactiveSurveySlice";
import {
  setFieldRules,
  setLeaseComps,
  setLoading,
} from "@/features/leaseComps/leaseCompsSlice";
import { setMarinePorts } from "@/features/media/mediaSlice";
import { setCategories, setPois } from "@/features/poi/poiSlice";
import { setBingMap } from "@/features/survey/bingMapSlice";
import { useGetEmeraldBingTilesQuery } from "@/services/bingApi";
import {
  useGetLeaseCompFieldRuleQuery,
  useGetLeaseCompsQuery,
} from "@/services/leaseCompsService";
import { useFetchMarinePortsQuery } from "@/services/mapApi";
import { useGetSurveyMapKeysBySurveyQuery } from "@/services/mapKeysService";
import { useGetMediaByIdsQuery } from "@/services/mediaService";
import {
  useGetPoisCategoriesQuery,
  useGetPoisQuery,
} from "@/services/poiService";
import { useGetSurveyDetailsQuery } from "@/services/survey";
import {
  getSurveyGroupId,
  setActiveSurveyId,
} from "@/services/interactiveSurveyAuth";
import { useGetSurveyGroupWithSurveysQuery } from "@/services/surveyGroupService";
import { useMapKeyVisibility } from "@/shared/hooks/useMapKeyVisibility";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import type { CSSProperties } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import GroupTabStrip from "./GroupTabStrip";
import LoadingSpinner from "./LoadingSpinner";
import MenuWrapper from "./MenuBar/MenuWrapper";
/**
 * Interactive Survey Layout
 * Provides consistent layout with navigation menu for all protected survey routes
 * Fetches survey data once for all child routes
 */
const InteractiveSurveyLayout = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMarketMapRoute = location.pathname.includes("/marketmap");

  // The interactiveToken is scoped to whatever ID was used at login. For a
  // group session that's the surveyGroupId — sending a member survey's own ID
  // here would mismatch the token's scope and 401, so only sync surveyId for
  // standalone (non-group) sessions and let base.ts fall back to surveyGroupId.
  const surveyGroupId = getSurveyGroupId();
  useEffect(() => {
    if (surveyId && !surveyGroupId) {
      setActiveSurveyId(surveyId);
    }
  }, [surveyId, surveyGroupId]);

  // If this survey was reached via a group session, keep the group tab strip available
  const { currentSurvey, currentSurveyGroup, groupSurveys } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const { data: surveyGroupData } = useGetSurveyGroupWithSurveysQuery(
    surveyGroupId || "",
    { skip: !surveyGroupId },
  );

  useEffect(() => {
    if (surveyGroupData) {
      dispatch(setCurrentSurveyGroup(surveyGroupData));
    }
  }, [surveyGroupData, dispatch]);

  // Load persisted map key visibility once for all child routes
  useMapKeyVisibility();
  // Fetch survey details using RTK Query
  const {
    data: surveyData,
    error,
    isLoading,
  } = useGetSurveyDetailsQuery(surveyId!, {
    skip: !surveyId,
  });

  const { data: leaseComps, isLoading: isLeaseCompsLoading } =
    useGetLeaseCompsQuery(surveyId!, {
      skip: !surveyId,
    });

  const { data: leaseCompFieldRules, isLoading: isLeaseCompFieldRulesLoading } =
    useGetLeaseCompFieldRuleQuery(surveyId!, { skip: !surveyId });

  const { data: poisData } = useGetPoisQuery(
    surveyId!,
    { skip: !surveyId },
  );

  const { data: poiCategoriesData } = useGetPoisCategoriesQuery(surveyId!, {
    skip: !surveyId,
  });

  // Fetch media when survey data is available
  const propertyIds = surveyData?.properties
    ?.map((prop) => prop.propertyId)
    .filter((id): id is string => id !== undefined);

  useGetMediaByIdsQuery(
    propertyIds && propertyIds.length > 0 ? { ids: propertyIds } : skipToken,
  );

  const leaseCompIds = leaseComps?.map((comp) => comp.leaseCompId);

  useGetMediaByIdsQuery(
    leaseCompIds && leaseCompIds.length > 0
      ? { ids: leaseCompIds, propertyType: "LEASE_COMP" }
      : skipToken,
  );

  const { currentBingMap } = useAppSelector((state) => state.bingMap);
  const { data: bingTilesData } = useGetEmeraldBingTilesQuery();
  const { data: marinePortsRes } = useFetchMarinePortsQuery();
  useGetSurveyMapKeysBySurveyQuery(surveyId!);

  const handleBingMapSetup = () => {
    if (!currentBingMap && bingTilesData) {
      dispatch(setBingMap(bingTilesData));
    }
  };

  useEffect(() => {
    if (!marinePortsRes) return;
    dispatch(setMarinePorts(marinePortsRes.features || []));
  }, [marinePortsRes]);

  useEffect(() => {
    handleBingMapSetup();
  }, [bingTilesData]);

  // Store survey data in Redux when it's loaded
  useEffect(() => {
    if (surveyData) {
      dispatch(setCurrentSurvey(surveyData));
    }
  }, [surveyData, dispatch]);

  useEffect(() => {
    if (isLeaseCompsLoading) {
      dispatch(setLoading(true));
    } else if (leaseComps) {
      dispatch(setLeaseComps(leaseComps));
    }
  }, [leaseComps, isLeaseCompsLoading]);

  useEffect(() => {
    if (isLeaseCompFieldRulesLoading) {
      dispatch(setLoading(true));
    } else if (leaseCompFieldRules) {
      dispatch(setFieldRules(leaseCompFieldRules));
    }
  }, [leaseCompFieldRules, isLeaseCompFieldRulesLoading]);

  useEffect(() => {
    if (poiCategoriesData) {
      dispatch(setCategories(poiCategoriesData));
    }
  }, [poiCategoriesData]);

  useEffect(() => {
    if (poisData) {
      dispatch(setPois(poisData));
    }
  }, [poisData]);

  // Handle errors by redirecting to login — but a group-session user should
  // never get bounced to the single-survey login; keep them within the group.
  useEffect(() => {
    if (error && surveyId && !location.pathname.includes("/login")) {
      dispatch(setError("Failed to load survey details"));
      if (surveyGroupId) {
        navigate(`/interactive/group/${surveyGroupId}/cover`);
      } else {
        navigate(`/interactive/${surveyId}/login`);
      }
    }
  }, [error, dispatch, navigate, surveyId, surveyGroupId, location.pathname]);

  // Cached survey data can render before the store is updated, leaving the
  // previous market's survey in place; child routes would then fail to find
  // their property and redirect or flip filters (visible as flicker).
  const isSurveyStale = Boolean(surveyData) && currentSurvey?.id !== surveyId;

  // Show loading state while fetching survey data
  if (isLoading || isSurveyStale) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading survey..." />
      </div>
    );
  }

  const hasGroupStrip = Boolean(surveyGroupId && currentSurveyGroup);

  return (
    <main
      className="min-h-screen bg-gray-50"
      style={
        {
          "--survey-header-offset": hasGroupStrip ? "9rem" : "4.5rem",
        } as CSSProperties
      }
    >
      {/* Header */}
      {hasGroupStrip && (
        <GroupTabStrip
          surveyGroup={currentSurveyGroup!}
          surveys={groupSurveys ?? []}
        />
      )}
      <MenuWrapper hideBranding={hasGroupStrip} />
      {/* <Header /> */}

      {/* Main Content */}
      <section
        className={`flex-1 flex flex-col ${!isMarketMapRoute ? "overflow-y-auto" : ""}`}
        style={{ height: `calc(100vh - ${hasGroupStrip ? "9rem" : "4.5rem"})` }}
      >
        <Outlet />
      </section>
    </main>
  );
};

export default InteractiveSurveyLayout;
